import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextField } from '@mui/material';
import { Search } from '@mui/icons-material';
import { Button } from '@mui/material';
import {
  SectionStyled,
  SectionHeaderStyled,
  SectionTitleStyled,
  SectionDescriptionStyled,
  LinearProgressStyled,
  NoDataMessageStyled,
  SearchInputContainerStyled,
} from '../RestoreConnectionsPage.styles';
import type { AdminOwnerEmployer } from 'app/types/restoreConnections.types';
import { FilterableTable } from 'app/components';
import type { FilterableTableColumnType, FilterableTableRowMapperFunc } from 'app/components';
import { TranslationKeys } from 'app/translations';

enum EmployersTableColumns {
  EmployerName = 'employerName',
  ContactEmail = 'contactEmail',
  ContactName = 'contactName',
  EmployerEmail = 'employerEmail',
  Actions = 'actions',
}

interface EmployersTableProps {
  data: AdminOwnerEmployer[] | undefined;
  loading: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSelectEmployer: (employer: AdminOwnerEmployer) => void;
  pagination: { page: number; rowsPerPage: number; totalCount: number };
  onPaginationChange: ({ page, rowSize }: { page?: number; rowSize?: number }) => void;
}

export const EmployersTable: React.FC<EmployersTableProps> = ({
  data,
  loading,
  searchQuery,
  onSearchChange,
  onSelectEmployer,
  pagination,
  onPaginationChange,
}) => {
  const intl = useIntl();

  const employersTableHeaders = React.useMemo<Array<FilterableTableColumnType>>(() => {
    return [
      {
        name: EmployersTableColumns.EmployerName,
        title: <FormattedMessage id={TranslationKeys.employers_employerName} />,
      },
      {
        name: EmployersTableColumns.ContactEmail,
        title: <FormattedMessage id={TranslationKeys.employers_contactEmail} />,
      },
      {
        name: EmployersTableColumns.ContactName,
        title: <FormattedMessage id={TranslationKeys.employers_contactName} />,
      },
      {
        name: EmployersTableColumns.EmployerEmail,
        title: <FormattedMessage id={TranslationKeys.employers_employerEmail} />,
      },
      {
        name: EmployersTableColumns.Actions,
        title: '',
      },
    ];
  }, []);

  const employersRowMapper = React.useCallback<FilterableTableRowMapperFunc<any>>(
    (data: AdminOwnerEmployer) => {
      return {
        data: {
          [EmployersTableColumns.EmployerName]: data.employerName,
          [EmployersTableColumns.ContactEmail]: data.contactEmail || '-',
          [EmployersTableColumns.ContactName]: data.contactName || '-',
          [EmployersTableColumns.EmployerEmail]: data.employerEmail || '-',
          [EmployersTableColumns.Actions]: (
            <Button variant="outlined" size="small" onClick={() => onSelectEmployer(data)}>
              <FormattedMessage id={TranslationKeys.tools_restoreConnections_viewConnections} />
            </Button>
          ),
        },
      };
    },
    [onSelectEmployer],
  );

  return (
    <SectionStyled elevation={1}>
      <SectionHeaderStyled>
        <SectionDescriptionStyled variant="body2">
          <FormattedMessage id={TranslationKeys.tools_restoreConnections_description} />
        </SectionDescriptionStyled>
      </SectionHeaderStyled>
      <SearchInputContainerStyled>
        <SectionTitleStyled variant="h5">
          <FormattedMessage id={TranslationKeys.employers} />
        </SectionTitleStyled>
        <TextField
          variant="outlined"
          size="medium"
          placeholder={intl.formatMessage({
            id: TranslationKeys.tools_restoreConnections_selectEmployerPlaceholder,
          })}
          value={searchQuery}
          onChange={event => onSearchChange(event.target.value)}
          InputProps={{
            startAdornment: <Search style={{ marginRight: '8px' }} />,
          }}
        />
      </SearchInputContainerStyled>

      {loading ? (
        <LinearProgressStyled />
      ) : data && data.length > 0 ? (
        <FilterableTable
          columns={employersTableHeaders}
          rows={data}
          loading={loading}
          rowMapper={employersRowMapper}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
        />
      ) : (
        <NoDataMessageStyled>
          <FormattedMessage id={TranslationKeys.tools_restoreConnections_noEmployersFound} />
        </NoDataMessageStyled>
      )}
    </SectionStyled>
  );
};
